import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  SearchField,
  Spacing,
  StyledDiv,
  StyledFixedButtonWrapper,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';

export interface Coach {
  id: number;
  name: string;
  disabled?: boolean;
  disabledReason?: string;
}

export interface RmAssignCoachesProps {
  schoolName: string;
  emis: string;
  coaches: Coach[];
  selectedCoachIds?: number[];
  onCoachSelect: (coachIds: number[]) => void;
  searchValue: string;
  onSearchChange: (val: string) => void;
  onSave: () => void;
  onCoachRowClick: (coach: Coach) => void;
}

export const RmAssignCoaches = ({
  schoolName,
  emis,
  coaches,
  selectedCoachIds = [],
  onCoachSelect,
  searchValue,
  onSearchChange,
  onSave,
  onCoachRowClick,
}: RmAssignCoachesProps) => {
  const handleCoachToggle = (coachId: number) => {
    const coach = coaches.find((c) => c.id === coachId);

    if (coach?.disabled) {
      return; // Prevent toggling disabled coaches
    }

    const isSelected = selectedCoachIds.includes(coachId);
    let newSelectedCoaches: number[];

    if (isSelected) {
      // Remove coach from selection
      newSelectedCoaches = selectedCoachIds.filter((id) => id !== coachId);
    } else {
      // Add coach to selection
      newSelectedCoaches = [...selectedCoachIds, coachId];
    }

    onCoachSelect(newSelectedCoaches);
  };

  return (
    <StyledDiv
      flexDirection="column"
      width="100%"
      padding="0 0 var(--rk-spacing-xxxxxl) 0"
    >
      <Typography variant="textLabelLarge">School</Typography>
      <Typography variant="textHeadlineMedium">
        EMIS {emis} - {schoolName}
      </Typography>
      <Spacing marginBottom="xl" />
      <Typography variant="textTitleLarge">Assign coaches</Typography>
      <Typography
        variant="textBodyMedium"
        color="var(--md-sys-color-on-surface-variant)"
      >
        You can select multiple coaches for a school
      </Typography>
      <Spacing marginBottom="lg" />
      <StyledSearchFieldWrapper>
        <SearchField
          variant="outlined"
          value={searchValue}
          onChange={onSearchChange}
          label="Search Coach"
        />
      </StyledSearchFieldWrapper>
      <Spacing marginBottom="xxl" />
      <StyledDiv flexDirection="column" borderRadius="8px">
        <StyledDiv
          padding="var(--rk-spacing-xl)"
          background="--md-sys-color-surface-container-low"
        >
          <Typography variant="textLabelLarge">
            Coaches ({selectedCoachIds.length} selected)
          </Typography>
        </StyledDiv>

        <StyledListCont>
          {coaches.map((coach) => {
            return (
              <CoachRow
                key={coach.id}
                onClick={() => {
                  if (!coach.disabled) {
                    onCoachRowClick(coach);
                  }
                }}
                disabled={coach.disabled}
              >
                <StyledDiv flexDirection="column">
                  <Typography variant="textBodyLarge">{coach.name}</Typography>
                  {coach.disabled && coach.disabledReason && (
                    <Typography
                      variant="textBodySmallOnSurfaceVariant"
                      color="var(--md-sys-color-on-surface-variant)"
                    >
                      {coach.disabledReason}
                    </Typography>
                  )}
                </StyledDiv>

                <Checkbox
                  checked={selectedCoachIds.includes(coach.id)}
                  onChange={() => {
                    handleCoachToggle(coach.id);
                  }}
                  disabled={coach.disabled}
                  label=""
                  stateLess
                />
              </CoachRow>
            );
          })}
        </StyledListCont>
      </StyledDiv>
      <Spacing marginBottom="xl" />
      <StyledFixedButtonWrapper>
        <Button onClick={onSave}>Save</Button>
      </StyledFixedButtonWrapper>
    </StyledDiv>
  );
};

const CoachRow = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--rk-spacing-md) 0 var(--rk-spacing-md) var(--rk-spacing-xl);
  border-bottom: 1px solid var(--md-sys-color-outline);
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  &:last-child {
    border-bottom: none;
  }
  ${mq(BreakpointSizes.desktop)} {
    width: 50%;
  }
`;

const StyledSearchFieldWrapper = styled.div`
  width: 100%;
  ${mq(BreakpointSizes.desktop)} {
    width: 50%;
  }
`;

const StyledListCont = styled.div`
  display: flex;
  flex-direction: column;

  ${mq(BreakpointSizes.desktop)} {
    padding: 0 0 var(--rk-spacing-xxl) 0;
  }
`;
